// Shared presentation validator (presentation contract sections 2-5 and 7.2). The client and
// the Node tools run the same checks. Structural checks need only the package; reference checks
// also take a Describe result and recorded observations.

import { parseBinding, expandAsset } from './bindings.ts';
import { parseSelector, usesSeat, type Selector } from './selectors.ts';
import {
  BUILTIN_GLYPHS, CARD_STYLES, COLOR_TOKENS, FONT_NAMES, MATERIALS, SEAT_PIECES, SHAPE_SLOTS, TEXT_ROLES,
  STANDEE_MANIFEST_FIELDS, type Distribution, type ThemeV2, type Interactions, type LayoutNode, type Layouts, type Manifest, type Templates, type Theme,
} from './types.ts';
import { GAME_ICONS } from '../icons/gameIcons.ts';
import { COLOR_ICONS } from '../icons/colorIcons.ts';
import { STANDEE_BUDGET } from '../boards/three/standee/types.ts';
import { STANDEE_ID } from '../boards/three/standee/ids.ts';

export interface Issue { file: string; path: string; message: string; severity: 'error' | 'warning' }

export const ID = /^[A-Za-z0-9][A-Za-z0-9:_.\-]{0,127}$/;
const VERSION = /^\d+\.\d+\.\d+$/;
const SHA = /^[0-9a-f]{64}$/;
export const HEX = /^#[0-9a-fA-F]{6}$/;
/** three-board model limits (docs/presentation-package-v2-contract.md, 3D models). */
export const MODEL_MAX_COUNT = 32, MODEL_MAX_BYTES = 4 * 1024 * 1024, MODEL_MAX_TOTAL = 16 * 1024 * 1024;
export const MODEL_ID = /^[a-z][a-z0-9-]{0,63}$/;
const MODEL_PATH = /^(?!.*(?:^|\/)\.\.?(?:\/|$))[A-Za-z0-9_][A-Za-z0-9_.\/-]*\.glb$/;
const STANDEE_PATH = /^(?!.*(?:^|\/)\.\.?(?:\/|$))[A-Za-z0-9_][A-Za-z0-9_.\/-]*\.standee$/;
const STANDEE_FRONT = /^[A-Za-z0-9][A-Za-z0-9:_.\/-]{0,127}$/;
const RULES_PATTERN = /^(\d+\.\d+\.\d+|\d+\.\*|\d+\.\d+\.\*)$/;
const CAPABILITIES = ['cards', 'board-graph', 'board-regions', 'allocation', 'ordering', 'images'];
const OBJECT_KINDS = ['card', 'token', 'worker', 'tile', 'space', 'node', 'edge', 'marker', 'die'];
const ZONE_KINDS = ['hand', 'row', 'grid', 'stack', 'area', 'track', 'board', 'supply', 'discard'];
const COMPONENTS = ['zone', 'board', 'counters', 'seat-header', 'status', 'choices', 'inspector', 'log', 'result'];
const CONTROLS = ['list', 'buttons', 'in-place', 'ordered', 'payment', 'stepper', 'textarea'];
const STEP_TYPES = ['pick', 'allocate', 'number', 'text', 'record'];
const ASSET_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** Player-selectable layouts (docs/ui-variants-design.md): the id of a `presentations[]` entry that has a `variant`. */
export const VARIANT_ID = /^[a-z][a-z0-9-]{0,31}$/;
export const FORM_FACTORS = ['any', 'phone', 'desktop'] as const;
export const VARIANT_FIELDS = ['variant', 'label', 'summary', 'formFactor'] as const;

export class Checker {
  readonly issues: Issue[] = [];
  private readonly file: string;
  constructor(file: string) { this.file = file; }
  error(path: string, message: string) { this.issues.push({ file: this.file, path, message, severity: 'error' }); }
  warn(path: string, message: string) { this.issues.push({ file: this.file, path, message, severity: 'warning' }); }
  object(value: unknown, path: string, required: string[], optional: string[] = []): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) { this.error(path, 'Expected an object.'); return false; }
    const allowed = new Set([...required, ...optional]);
    for (const key of Object.keys(value)) if (!allowed.has(key)) this.error(`${path}.${key}`, 'Unknown field.');
    for (const key of required) if (!(key in value)) this.error(`${path}.${key}`, 'Required field is missing.');
    return true;
  }
  string(value: unknown, path: string, max = 256, pattern?: RegExp): value is string {
    if (typeof value !== 'string' || value.length === 0 || value.length > max) { this.error(path, `Expected text of 1 to ${max} characters.`); return false; }
    if (pattern && !pattern.test(value)) { this.error(path, 'Has an invalid format.'); return false; }
    return true;
  }
  array(value: unknown, path: string, min: number, max: number): value is unknown[] {
    if (!Array.isArray(value) || value.length < min || value.length > max) { this.error(path, `Expected a list of ${min} to ${max} entries.`); return false; }
    return true;
  }
  number(value: unknown, path: string, min: number, max: number, integer = false): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max || (integer && !Number.isInteger(value))) {
      this.error(path, integer ? `Expected an integer from ${min} to ${max}.` : `Expected a number from ${min} to ${max}.`); return false;
    }
    return true;
  }
  oneOf(value: unknown, path: string, options: readonly string[]): boolean {
    if (typeof value !== 'string' || !options.includes(value)) { this.error(path, `Expected one of: ${options.join(', ')}.`); return false; }
    return true;
  }
}

/** A relative path that stays inside its base directory (both use `/`). */
export function safeRelative(base: string, path: string): string | null {
  if (path.startsWith('/') || path.includes('\\') || /^[A-Za-z]:/.test(path)) return null;
  const parts = base ? base.split('/').filter(Boolean) : [];
  for (const part of path.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') { if (parts.length === 0) return null; parts.pop(); }
    else parts.push(part);
  }
  return parts.join('/');
}

export function validateDistribution(value: unknown): Issue[] {
  const c = new Checker('distribution.json');
  if (!c.object(value, '$', ['formatVersion', 'id', 'version', 'title', 'rules', 'presentations'])) return c.issues;
  const d = value as unknown as Distribution;
  if (d.formatVersion !== 'distribution-1') c.error('$.formatVersion', 'Expected "distribution-1".');
  c.string(d.id, '$.id', 128, ID); c.string(d.version, '$.version', 32, VERSION); c.string(d.title, '$.title', 128);
  if (c.array(d.rules, '$.rules', 1, 32)) {
    const ids = new Set<string>(); let defaults = 0;
    d.rules.forEach((r, i) => {
      const p = `$.rules[${i}]`;
      if (!c.object(r, p, ['id', 'label', 'path', 'sha256'], ['default'])) return;
      if (c.string(r.id, `${p}.id`, 128, ID) && ids.has(r.id)) c.error(`${p}.id`, 'Duplicate variant ID.'); ids.add(r.id);
      c.string(r.label, `${p}.label`, 128);
      if (c.string(r.path, `${p}.path`, 512) && safeRelative('', r.path) === null) c.error(`${p}.path`, 'Path leaves the distribution.');
      c.string(r.sha256, `${p}.sha256`, 64, SHA);
      if (r.default !== undefined && typeof r.default !== 'boolean') c.error(`${p}.default`, 'Expected true or false.');
      if (r.default) defaults++;
    });
    if (defaults !== 1) c.error('$.rules', 'Exactly one rules variant must be the default.');
  }
  if (c.array(d.presentations, '$.presentations', 0, 8)) {
    let defaults = 0, variants = 0;
    // A default entry without a `variant` is selectable as "default"; the id is reserved for it.
    const variantIds = new Set<string>();
    const paths = new Set<string>();
    d.presentations.forEach((r, i) => {
      const p = `$.presentations[${i}]`;
      if (!c.object(r, p, ['path', 'sha256'], ['default', ...VARIANT_FIELDS])) return;
      if (c.string(r.path, `${p}.path`, 512) && safeRelative('', r.path) === null) c.error(`${p}.path`, 'Path leaves the distribution.');
      if (typeof r.path === 'string') { if (paths.has(r.path)) c.error(`${p}.path`, 'Duplicate presentation path.'); paths.add(r.path); }
      c.string(r.sha256, `${p}.sha256`, 64, SHA);
      if (r.default !== undefined && typeof r.default !== 'boolean') c.error(`${p}.default`, 'Expected true or false.');
      if (r.default) defaults++;
      if (r.variant === undefined) {
        for (const key of ['label', 'summary', 'formFactor'] as const) if (r[key] !== undefined) c.error(`${p}.${key}`, 'Only a selectable presentation (with a "variant") has this field.');
        if (r.default) { if (variantIds.has('default')) c.error(`${p}.default`, 'Another layout already uses the id "default".'); variantIds.add('default'); }
        return;
      }
      variants++;
      if (c.string(r.variant, `${p}.variant`, 32, VARIANT_ID)) {
        if (variantIds.has(r.variant)) c.error(`${p}.variant`, 'Duplicate layout ID.');
        variantIds.add(r.variant);
      }
      c.string(r.label, `${p}.label`, 40);
      if (r.summary !== undefined) c.string(r.summary, `${p}.summary`, 160);
      if (r.formFactor !== undefined) c.oneOf(r.formFactor, `${p}.formFactor`, FORM_FACTORS);
    });
    if (defaults > 1) c.error('$.presentations', 'At most one presentation may be the default.');
    if (variants > 0 && defaults === 0) c.error('$.presentations', 'Selectable layouts need a default presentation.');
  }
  return c.issues;
}

const V1_FILES = ['layouts', 'templates', 'interactions', 'theme'];
const V2_FILES = ['scene', 'targets', 'templates', 'interactions', 'theme', 'layouts', 'faces', 'atlases', 'art', 'boards'];
const V2_CAPABILITIES = [...CAPABILITIES, 'scene', 'direct-play', 'motion', 'materials', 'faces', 'atlases', 'hosted-art', 'complex-board', 'records', 'pixi-board', 'edge-pieces', 'rich-text', 'catalog-guide', 'seat-ring', 'three-board', 'three-helpers'];

/** Validates a presentation-1 or presentation-2 manifest; the format decides the file keys and capabilities. */
export function validateManifest(value: unknown): Issue[] {
  const c = new Checker('manifest.json');
  if (!c.object(value, '$', ['formatVersion', 'id', 'version', 'label', 'compatible', 'requires', 'files', 'assets'], ['artRelease', 'module', 'models', 'standees'])) return c.issues;
  const m = value as unknown as Manifest;
  const v2 = (m.formatVersion as string) === 'presentation-2';
  const module = (value as { module?: { api: string; path: string; sha256: string } }).module;
  if (module !== undefined) {
    if (!v2) c.error('$.module', 'UI modules require presentation-2.');
    if (c.object(module, '$.module', ['api', 'path', 'sha256'])) {
      c.string(module.api, '$.module.api', 8, /^ui-sdk-1$/);
      c.string(module.path, '$.module.path', 256, /^(?!.*(?:^|\/)\.\.?(?:\/|$))[A-Za-z0-9_./-]+\.mjs$/);
      c.string(module.sha256, '$.module.sha256', 64, SHA);
    }
  }
  const models = (value as { models?: unknown }).models;
  if (Array.isArray(m.requires) && (m.requires as string[]).includes('three-board') && !(m.requires as string[]).includes('complex-board')) c.error('$.requires', 'three-board requires the complex-board capability.');
  if (Array.isArray(m.requires) && (m.requires as string[]).includes('three-helpers') && !(m.requires as string[]).includes('three-board')) c.error('$.requires', 'three-helpers requires the three-board capability.');
  if (models !== undefined) {
    if (!v2 || !Array.isArray(m.requires) || !(m.requires as string[]).includes('three-board')) c.error('$.models', '3D models require presentation-2 and the three-board capability.');
    let total = 0;
    if (c.array(models, '$.models', 0, MODEL_MAX_COUNT)) {
      const ids = new Set<string>(), paths = new Set<string>();
      models.forEach((entry, i) => {
        const p = `$.models[${i}]`;
        if (!c.object(entry, p, ['id', 'path', 'sha256', 'bytes', 'license'], ['attribution'])) return;
        if (c.string(entry.id, p + '.id', 64, MODEL_ID) && ids.has(entry.id)) c.error(p + '.id', 'Duplicate model ID.'); if (typeof entry.id === 'string') ids.add(entry.id);
        if (c.string(entry.path, p + '.path', 256, MODEL_PATH)) { const base = entry.path.slice(entry.path.lastIndexOf('/') + 1); if (paths.has(base)) c.error(p + '.path', 'Model file names must be distinct.'); paths.add(base); }
        c.string(entry.sha256, p + '.sha256', 64, SHA);
        if (c.number(entry.bytes, p + '.bytes', 20, MODEL_MAX_BYTES, true)) total += entry.bytes;
        c.string(entry.license, p + '.license', 256);
        if (entry.attribution !== undefined) c.string(entry.attribution, p + '.attribution', 512);
      });
      if (total > MODEL_MAX_TOTAL) c.error('$.models', 'Models exceed 16 MiB in total.');
    }
  }
  const standees = (value as { standees?: unknown }).standees;
  if (standees !== undefined) {
    const requires = Array.isArray(m.requires) ? (m.requires as string[]) : [];
    if (!v2 || !['three-board', 'three-helpers', 'hosted-art'].every(r => requires.includes(r)) || (value as { artRelease?: unknown }).artRelease === undefined) {
      c.error('$.standees', 'Standees require presentation-2, the three-helpers and hosted-art capabilities and an artRelease.');
    }
    if (c.array(standees, '$.standees', 0, STANDEE_BUDGET.maxCount)) {
      const ids = new Set<string>(), names = new Set<string>(Array.isArray(models) ? models.map(e => (typeof e?.path === 'string' ? e.path.slice(e.path.lastIndexOf('/') + 1) : '')) : []);
      let total = 0;
      standees.forEach((entry, i) => {
        const p = `$.standees[${i}]`;
        if (!c.object(entry, p, [...STANDEE_MANIFEST_FIELDS.required], [...STANDEE_MANIFEST_FIELDS.optional])) return;
        if (c.string(entry.id, p + '.id', 56, STANDEE_ID) && ids.has(entry.id)) c.error(p + '.id', 'Duplicate standee ID.');
        if (typeof entry.id === 'string') ids.add(entry.id);
        if (c.string(entry.path, p + '.path', 256, STANDEE_PATH)) {
          const base = entry.path.slice(entry.path.lastIndexOf('/') + 1);
          if (names.has(base)) c.error(p + '.path', 'Standee file names must be distinct.');
          names.add(base);
        }
        c.string(entry.sha256, p + '.sha256', 64, SHA);
        if (c.number(entry.bytes, p + '.bytes', 32, STANDEE_BUDGET.maxFileBytes, true)) total += entry.bytes;
        c.string(entry.front, p + '.front', 128, STANDEE_FRONT);
        const b = entry.artBounds;
        if (!Array.isArray(b) || b.length !== 4 || !b.every(n => typeof n === 'number' && Number.isFinite(n)) || !((b[2] as number) > (b[0] as number)) || !((b[3] as number) > (b[1] as number))) {
          c.error(p + '.artBounds', 'artBounds must be [minX, minY, maxX, maxY] with max above min.');
        }
        c.number(entry.height, p + '.height', 0.05, 8);
        c.oneOf(entry.mounting, p + '.mounting', ['upright', 'ground']);
        c.oneOf(entry.facing, p + '.facing', ['fixed', 'camera']);
        if (typeof entry.edge !== 'string' || (entry.edge !== 'art' && entry.edge !== 'owner' && !HEX.test(entry.edge))) c.error(p + '.edge', 'edge must be art, owner or #rrggbb.');
        if (entry.mounting === 'ground' && entry.facing === 'camera') c.error(p + '.facing', 'facing camera needs an upright standee.');
      });
      if (total > STANDEE_BUDGET.maxTotalBytes) c.error('$.standees', 'Standees exceed 16 MiB in total.');
    }
  }
  const art = (value as { artRelease?: { id: string; sha256: string } }).artRelease;
  if (art !== undefined) {
    if (!v2 || !Array.isArray(m.requires) || !(m.requires as string[]).includes('hosted-art')) c.error('$.artRelease', 'Hosted art requires presentation-2 and the hosted-art capability.');
    if (c.object(art, '$.artRelease', ['id', 'sha256'])) {
      c.string(art.id, '$.artRelease.id', 64, /^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/);
      c.string(art.sha256, '$.artRelease.sha256', 64, SHA);
    }
  }
  if (m.formatVersion !== 'presentation-1' && !v2) c.error('$.formatVersion', 'Expected "presentation-1" or "presentation-2".');
  c.string(m.id, '$.id', 128, ID); c.string(m.version, '$.version', 32, VERSION); c.string(m.label, '$.label', 128);
  if (c.object(m.compatible, '$.compatible', ['gameId', 'rulesVersions', 'ui'])) {
    c.string(m.compatible.gameId, '$.compatible.gameId', 64, ID);
    if (c.array(m.compatible.rulesVersions, '$.compatible.rulesVersions', 1, 16))
      m.compatible.rulesVersions.forEach((v, i) => c.string(v, `$.compatible.rulesVersions[${i}]`, 32, RULES_PATTERN));
    if (m.compatible.ui !== 'ui-2') c.error('$.compatible.ui', 'Expected "ui-2".');
  }
  const capabilities = v2 ? V2_CAPABILITIES : CAPABILITIES;
  if (c.array(m.requires, '$.requires', 0, capabilities.length)) m.requires.forEach((r, i) => c.oneOf(r, `$.requires[${i}]`, capabilities));
  if (Array.isArray(m.requires) && new Set(m.requires).size !== m.requires.length) c.error('$.requires', 'Capabilities must be unique.');
  const required = v2 ? ['scene', 'theme'] : V1_FILES;
  const keys = v2 ? V2_FILES : V1_FILES;
  if (c.object(m.files, '$.files', required, keys.filter(k => !required.includes(k)))) {
    if ('boards' in m.files && (!Array.isArray(m.requires) || !(m.requires as string[]).includes('complex-board'))) c.error('$.files.boards', 'Board visuals require the complex-board capability.');
    for (const key of keys) {
      const f = (m.files as Record<string, { path: string; sha256: string } | undefined>)[key]; const p = `$.files.${key}`;
      if (f === undefined) continue;
      if (!c.object(f, p, ['path', 'sha256'])) continue;
      if (c.string(f.path, `${p}.path`, 512) && f.path.includes('\\')) c.error(`${p}.path`, 'Use forward slashes.');
      c.string(f.sha256, `${p}.sha256`, 64, SHA);
    }
  }
  if (c.array(m.assets, '$.assets', 0, 2048)) {
    const ids = new Set<string>();
    m.assets.forEach((a, i) => {
      const p = `$.assets[${i}]`;
      if (!c.object(a, p, ['id', 'path', 'type', 'width', 'height', 'sha256', 'license', 'fallback'], ['attribution'])) return;
      if (c.string(a.id, `${p}.id`, 128, /^[A-Za-z0-9][A-Za-z0-9:_.\-/]{0,127}$/) && ids.has(a.id)) c.error(`${p}.id`, 'Duplicate asset ID.'); ids.add(a.id);
      c.string(a.path, `${p}.path`, 512); c.oneOf(a.type, `${p}.type`, ASSET_TYPES);
      c.number(a.width, `${p}.width`, 1, 4096, true); c.number(a.height, `${p}.height`, 1, 4096, true);
      c.string(a.sha256, `${p}.sha256`, 64, SHA); c.string(a.license, `${p}.license`, 128, /^[A-Za-z0-9][A-Za-z0-9.+-]*$/);
      if (a.attribution !== undefined) c.string(a.attribution, `${p}.attribution`, 512);
      if (c.string(a.fallback, `${p}.fallback`, 32) && !isShapeOrGlyph(a.fallback)) c.error(`${p}.fallback`, 'Use a built-in shape or glyph name.');
    });
  }
  return c.issues;
}

function isShapeOrGlyph(name: string): boolean {
  return name in SHAPE_SLOTS || (BUILTIN_GLYPHS as readonly string[]).includes(name);
}

export function validateLayouts(value: unknown): Issue[] {
  const c = new Checker('layouts.json');
  if (!c.object(value, '$', ['layouts'])) return c.issues;
  const l = value as unknown as Layouts;
  if (!c.object(l.layouts, '$.layouts', ['wide'], ['narrow'])) return c.issues;
  for (const name of ['wide', 'narrow'] as const) {
    const node = l.layouts[name];
    if (node === undefined) continue;
    const count = { nodes: 0, rest: 0 };
    layoutNode(c, node, `$.layouts.${name}`, 0, false, count);
    if (count.nodes > 256) c.error(`$.layouts.${name}`, 'A layout may have at most 256 nodes.');
    if (count.rest > 1) c.error(`$.layouts.${name}`, 'A layout may use the "rest" selector only once.');
    if (count.rest === 0) c.warn(`$.layouts.${name}`, 'No "rest" selector: the client adds a "More" panel for unplaced zones.');
  }
  return c.issues;
}

function layoutNode(c: Checker, node: unknown, path: string, depth: number, inRepeat: boolean, count: { nodes: number; rest: number }): void {
  count.nodes++;
  if (depth > 12) { c.error(path, 'Layouts may nest at most 12 levels.'); return; }
  if (!node || typeof node !== 'object') { c.error(path, 'Expected a layout node.'); return; }
  const n = node as Record<string, unknown>;
  switch (n.type) {
    case 'row': case 'column': {
      if (!c.object(n, path, ['type', 'children'], ['weights', 'gap'])) return;
      if (c.array(n.children, `${path}.children`, 1, 64)) (n.children as unknown[]).forEach((ch, i) => layoutNode(c, ch, `${path}.children[${i}]`, depth + 1, inRepeat, count));
      if (n.weights !== undefined && (!Array.isArray(n.weights) || n.weights.length !== (n.children as unknown[])?.length || n.weights.some(w => typeof w !== 'number' || w <= 0)))
        c.error(`${path}.weights`, 'Weights must be positive numbers, one per child.');
      if (n.gap !== undefined) c.oneOf(n.gap, `${path}.gap`, ['none', 's', 'm', 'l']);
      return;
    }
    case 'grid':
      if (!c.object(n, path, ['type', 'columns', 'children'])) return;
      c.number(n.columns, `${path}.columns`, 1, 12, true);
      if (c.array(n.children, `${path}.children`, 1, 64)) (n.children as unknown[]).forEach((ch, i) => layoutNode(c, ch, `${path}.children[${i}]`, depth + 1, inRepeat, count));
      return;
    case 'tabs':
      if (!c.object(n, path, ['type', 'tabs'])) return;
      if (c.array(n.tabs, `${path}.tabs`, 1, 12)) (n.tabs as unknown[]).forEach((t, i) => {
        const p = `${path}.tabs[${i}]`;
        if (c.object(t, p, ['label', 'child'])) { c.string(t.label, `${p}.label`, 64); layoutNode(c, t.child, `${p}.child`, depth + 1, inRepeat, count); }
      });
      return;
    case 'repeat':
      if (!c.object(n, path, ['type', 'over', 'order', 'child'])) return;
      c.oneOf(n.over, `${path}.over`, ['seats', 'opponents']); c.oneOf(n.order, `${path}.order`, ['self-first', 'seat-order']);
      if (inRepeat) c.error(path, 'A repeat cannot contain another repeat.');
      layoutNode(c, n.child, `${path}.child`, depth + 1, true, count);
      return;
    case 'panel':
      if (!c.object(n, path, ['type', 'child'], ['title', 'collapsible'])) return;
      if (n.title !== undefined) c.string(n.title, `${path}.title`, 64);
      if (n.collapsible !== undefined && typeof n.collapsible !== 'boolean') c.error(`${path}.collapsible`, 'Expected true or false.');
      layoutNode(c, n.child, `${path}.child`, depth + 1, inRepeat, count);
      return;
    case 'component': {
      if (!c.object(n, path, ['type', 'component'], ['select', 'props'])) return;
      if (!c.oneOf(n.component, `${path}.component`, COMPONENTS)) return;
      if (n.select !== undefined && c.string(n.select, `${path}.select`, 160)) {
        const s = parseSelector(n.select as string);
        if (typeof s === 'string') c.error(`${path}.select`, s);
        else {
          if (s.kind === 'rest') count.rest++;
          if (usesSeat(s) && !inRepeat) c.error(`${path}.select`, '"{seat}" is only valid inside a repeat.');
          checkSelectorFits(c, n.component as string, s, `${path}.select`);
        }
      }
      if (n.props !== undefined) componentProps(c, n.component as string, n.props, `${path}.props`);
      if (n.component === 'seat-header' && !inRepeat) c.error(path, 'seat-header belongs inside a repeat.');
      return;
    }
    default: c.error(`${path}.type`, 'Unknown layout node type.');
  }
}

function checkSelectorFits(c: Checker, component: string, s: Selector, path: string): void {
  const zoneSelectors = ['zone', 'zonePrefix', 'zonesOwner', 'zonesKind', 'rest'];
  if (component === 'zone' && !zoneSelectors.includes(s.kind)) c.error(path, 'A zone component needs a zone selector.');
  if (component === 'board' && s.kind !== 'board') c.error(path, 'A board component needs a board: selector.');
  if (component === 'counters' && s.kind !== 'countersOwner' && s.kind !== 'countersKey') c.error(path, 'A counters component needs a counters: selector.');
}

function componentProps(c: Checker, component: string, props: unknown, path: string): void {
  const allowed: Record<string, string[]> = {
    zone: ['display', 'columns', 'size', 'showEmpty'], board: ['fit', 'zoom'], counters: ['style'],
    choices: ['layout'], log: ['limit'],
  };
  if (!c.object(props, path, [], allowed[component] ?? [])) return;
  const p = props as Record<string, unknown>;
  if (p.display !== undefined) c.oneOf(p.display, `${path}.display`, ['auto', 'row', 'grid', 'fan', 'stack', 'list']);
  if (p.columns !== undefined) c.number(p.columns, `${path}.columns`, 1, 12, true);
  if (p.size !== undefined) c.oneOf(p.size, `${path}.size`, ['s', 'm', 'l']);
  if (p.showEmpty !== undefined && typeof p.showEmpty !== 'boolean') c.error(`${path}.showEmpty`, 'Expected true or false.');
  if (p.fit !== undefined) c.oneOf(p.fit, `${path}.fit`, ['contain', 'width']);
  if (p.zoom !== undefined && typeof p.zoom !== 'boolean') c.error(`${path}.zoom`, 'Expected true or false.');
  if (p.style !== undefined) c.oneOf(p.style, `${path}.style`, ['chips', 'table', 'meters']);
  if (p.layout !== undefined) c.oneOf(p.layout, `${path}.layout`, ['grouped', 'flat']);
  if (p.limit !== undefined) c.number(p.limit, `${path}.limit`, 10, 200, true);
}

export function validateTemplates(value: unknown, theme?: Theme, assetIds?: Set<string>): Issue[] {
  const c = new Checker('templates.json');
  if (!c.object(value, '$', ['templates'])) return c.issues;
  const t = value as unknown as Templates;
  if (!c.array(t.templates, '$.templates', 0, 512)) return c.issues;
  const ids = new Set<string>();
  t.templates.forEach((tpl, i) => {
    const p = `$.templates[${i}]`;
    if (!c.object(tpl, p, ['id', 'match', 'shape', 'slots'])) return;
    if (c.string(tpl.id, `${p}.id`, 128, ID) && ids.has(tpl.id)) c.error(`${p}.id`, 'Duplicate template ID.'); ids.add(tpl.id);
    if (c.object(tpl.match, `${p}.match`, [], ['kind', 'catalog', 'tags', 'zoneKind', 'face'])) {
      const m = tpl.match;
      if (m.kind !== undefined) c.oneOf(m.kind, `${p}.match.kind`, OBJECT_KINDS);
      if (m.catalog !== undefined && c.array(m.catalog, `${p}.match.catalog`, 1, 256)) m.catalog.forEach((k, j) => c.string(k, `${p}.match.catalog[${j}]`, 128, ID));
      if (m.tags !== undefined && c.array(m.tags, `${p}.match.tags`, 1, 16)) m.tags.forEach((k, j) => c.string(k, `${p}.match.tags[${j}]`, 128, ID));
      if (m.zoneKind !== undefined) c.oneOf(m.zoneKind, `${p}.match.zoneKind`, ZONE_KINDS);
      if (m.face !== undefined) c.oneOf(m.face, `${p}.match.face`, ['up', 'down']);
    }
    if (!c.oneOf(tpl.shape, `${p}.shape`, Object.keys(SHAPE_SLOTS))) return;
    if (!c.object(tpl.slots, `${p}.slots`, [], SHAPE_SLOTS[tpl.shape])) return;
    for (const [slot, text] of Object.entries(tpl.slots)) {
      const sp = `${p}.slots.${slot}`;
      if (!c.string(text, sp, 160)) continue;
      const b = parseBinding(text);
      if (typeof b === 'string') { c.error(sp, b); continue; }
      if (b.source.kind === 'asset' && assetIds && !b.source.id.includes('{') && !assetIds.has(b.source.id)) c.error(sp, `Asset "${b.source.id}" is not in the manifest.`);
      if (b.source.kind === 'icon' && theme && !theme.icons?.[b.source.key] && !(BUILTIN_GLYPHS as readonly string[]).includes(b.source.key)) c.error(sp, `Icon "${b.source.key}" is not in the theme.`);
      for (const f of b.formatters) if (f.kind === 'map' && theme && !theme.maps?.[f.name]) c.error(sp, `Theme map "${f.name}" does not exist.`);
    }
  });
  return c.issues;
}

export function validateInteractions(value: unknown): Issue[] {
  const c = new Checker('interactions.json');
  if (!c.object(value, '$', [], ['groups', 'controls', 'quickSubmit', 'preferFree', 'pickLists', 'forms', 'autoAnswer', 'tally', 'responseGroups'])) return c.issues;
  const v = value as unknown as Interactions;
  if (v.groups !== undefined && c.array(v.groups, '$.groups', 0, 64)) v.groups.forEach((g, i) => {
    const p = `$.groups[${i}]`;
    if (!c.object(g, p, ['group'], ['order', 'hotkey'])) return;
    c.string(g.group, `${p}.group`, 64);
    if (g.order !== undefined) c.number(g.order, `${p}.order`, -1000, 1000, true);
    if (g.hotkey !== undefined) c.string(g.hotkey, `${p}.hotkey`, 1, /^[a-z0-9]$/);
  });
  if (v.controls !== undefined && c.array(v.controls, '$.controls', 0, 128)) v.controls.forEach((r, i) => {
    const p = `$.controls[${i}]`;
    if (!c.object(r, p, ['match', 'control'])) return;
    if (c.object(r.match, `${p}.match`, [], ['type', 'field', 'keyPrefix', 'group', 'hasObjects'])) {
      if (r.match.type !== undefined) c.oneOf(r.match.type, `${p}.match.type`, STEP_TYPES);
      if (r.match.field !== undefined) c.string(r.match.field, `${p}.match.field`, 128);
      if (r.match.keyPrefix !== undefined && typeof r.match.keyPrefix !== 'string') c.error(`${p}.match.keyPrefix`, 'Expected text.');
      if (r.match.group !== undefined) c.string(r.match.group, `${p}.match.group`, 64);
      if (r.match.hasObjects !== undefined && typeof r.match.hasObjects !== 'boolean') c.error(`${p}.match.hasObjects`, 'Expected true or false.');
    }
    c.oneOf(r.control, `${p}.control`, CONTROLS);
  });
  if (v.quickSubmit !== undefined && typeof v.quickSubmit !== 'boolean') c.error('$.quickSubmit', 'Expected true or false.');
  if (v.preferFree !== undefined && typeof v.preferFree !== 'boolean') c.error('$.preferFree', 'Expected true or false.');
  if (v.pickLists !== undefined) {
    if (v.pickLists !== null && typeof v.pickLists === 'object' && !Array.isArray(v.pickLists)) {
      if (c.object(v.pickLists, '$.pickLists', [], ['describe']) && v.pickLists.describe !== undefined) c.oneOf(v.pickLists.describe, '$.pickLists.describe', ['adjacent-regions']);
    } else if (typeof v.pickLists !== 'boolean') c.error('$.pickLists', 'Expected true, false or an options object.');
  }
  if (v.forms !== undefined && c.array(v.forms, '$.forms', 0, 64)) v.forms.forEach((k, i) => c.string(k, `$.forms[${i}]`, 64));
  if (v.responseGroups !== undefined && c.array(v.responseGroups, '$.responseGroups', 0, 16)) v.responseGroups.forEach((g, i) => c.string(g, `$.responseGroups[${i}]`, 64));
  if (v.autoAnswer !== undefined && c.array(v.autoAnswer, '$.autoAnswer', 0, 8)) v.autoAnswer.forEach((r, i) => {
    const p = `$.autoAnswer[${i}]`;
    if (!c.object(r, p, ['kind'], ['delayMs', 'label'])) return;
    c.string(r.kind, `${p}.kind`, 64);
    if (r.delayMs !== undefined) c.number(r.delayMs, `${p}.delayMs`, 150, 5000, true);
    if (r.label !== undefined) c.string(r.label, `${p}.label`, 64);
  });
  if (v.tally !== undefined && c.array(v.tally, '$.tally', 0, 8)) v.tally.forEach((r, i) => {
    const p = `$.tally[${i}]`;
    if (!c.object(r, p, ['group', 'counter', 'fieldPrefix', 'units'], ['noun'])) return;
    c.string(r.group, `${p}.group`, 64);
    c.string(r.counter, `${p}.counter`, 128);
    c.string(r.fieldPrefix, `${p}.fieldPrefix`, 64);
    if (c.array(r.units, `${p}.units`, 1, 8)) r.units.forEach((u, j) => c.string(u, `${p}.units[${j}]`, 64));
    if (r.noun !== undefined) c.string(r.noun, `${p}.noun`, 32);
  });
  return c.issues;
}

const THEME_V2_KEYS = ['material', 'cardStyle', 'elevation', 'motion', 'fonts', 'seatPieces', 'textRoles'];

/** A theme icon value: a built-in glyph, a manifest asset, or (presentation-2) a `gi:` library icon. */
function iconValueKnown(v: string, assetIds: Set<string> | undefined, v2: boolean, iconFrames?: Set<string>): boolean {
  if ((BUILTIN_GLYPHS as readonly string[]).includes(v)) return true;
  if (v2 && v.startsWith('gi:')) return v.slice(3) in GAME_ICONS;
  if (v2 && v.startsWith('svg:')) return v.slice(4) in COLOR_ICONS;
  if (v2 && iconFrames?.has(v)) return true;
  return !assetIds || assetIds.has(v);
}

export function validateTheme(value: unknown, assetIds?: Set<string>, v2 = false, iconFrames?: Set<string>): Issue[] {
  const c = new Checker('theme.json');
  if (!c.object(value, '$', [], ['colors', 'seatColors', 'icons', 'maps', 'font', 'cardSize', ...(v2 ? THEME_V2_KEYS : [])])) return c.issues;
  const t = value as unknown as Theme;
  const colors = (o: unknown, p: string, allowDark: boolean) => {
    if (!c.object(o, p, [], allowDark ? [...COLOR_TOKENS, 'dark'] : [...COLOR_TOKENS])) return;
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
      if (k === 'dark') colors(v, `${p}.dark`, false);
      else c.string(v, `${p}.${k}`, 7, HEX);
    }
  };
  if (t.colors !== undefined) {
    colors(t.colors, '$.colors', true);
    for (const scheme of [t.colors, t.colors.dark]) {
      if (scheme?.ink && scheme?.surface && contrast(scheme.ink, scheme.surface) < 4.5)
        c.warn('$.colors', `Ink on surface contrast is ${contrast(scheme.ink, scheme.surface).toFixed(2)}:1, below 4.5:1; the client will use its own palette.`);
    }
  }
  if (t.seatColors !== undefined && c.object(t.seatColors, '$.seatColors', [], Object.keys(t.seatColors as object)))
    for (const [k, v] of Object.entries(t.seatColors)) { c.string(k, `$.seatColors key`, 128, ID); c.string(v, `$.seatColors.${k}`, 7, HEX); }
  if (t.icons !== undefined && c.object(t.icons, '$.icons', [], Object.keys(t.icons as object)))
    for (const [k, v] of Object.entries(t.icons)) {
      c.string(k, '$.icons key', 128, ID);
      if (c.string(v, `$.icons.${k}`, 128) && !iconValueKnown(v, assetIds, v2, iconFrames)) c.error(`$.icons.${k}`, v2 ? `"${v}" is not a bundled glyph, declared atlas frame or manifest asset.` : `"${v}" is neither a built-in glyph nor a manifest asset.`);
    }
  if (t.maps !== undefined && c.object(t.maps, '$.maps', [], Object.keys(t.maps as object)))
    for (const [name, map] of Object.entries(t.maps)) {
      c.string(name, '$.maps key', 128, ID);
      if (c.object(map, `$.maps.${name}`, [], Object.keys(map as object))) for (const [k, v] of Object.entries(map)) { c.string(k, `$.maps.${name} key`, 128); c.string(v, `$.maps.${name}.${k}`, 128); }
    }
  if (t.font !== undefined) c.oneOf(t.font, '$.font', ['sans', 'serif', 'rounded']);
  if (t.cardSize !== undefined) c.number(t.cardSize, '$.cardSize', 72, 240);
  if (v2) themeV2Fields(c, value as ThemeV2);
  return c.issues;
}

function themeV2Fields(c: Checker, t: ThemeV2): void {
  if (t.material !== undefined && c.object(t.material, '$.material', [], ['table', 'tint', 'mats', 'matTint'])) {
    if (t.material.table !== undefined) c.oneOf(t.material.table, '$.material.table', MATERIALS);
    if (t.material.mats !== undefined) c.oneOf(t.material.mats, '$.material.mats', MATERIALS);
    if (t.material.tint !== undefined) c.string(t.material.tint, '$.material.tint', 7, HEX);
    if (t.material.matTint !== undefined) c.string(t.material.matTint, '$.material.matTint', 7, HEX);
  }
  if (t.cardStyle !== undefined) c.oneOf(t.cardStyle, '$.cardStyle', CARD_STYLES);
  if (t.elevation !== undefined) c.oneOf(t.elevation, '$.elevation', ['flat', 'soft', 'deep']);
  if (t.motion !== undefined && c.object(t.motion, '$.motion', [], ['speed', 'opponentPace'])) {
    if (t.motion.speed !== undefined) c.oneOf(t.motion.speed, '$.motion.speed', ['calm', 'brisk', 'snappy']);
    if (t.motion.opponentPace !== undefined) c.number(t.motion.opponentPace, '$.motion.opponentPace', 0.5, 2);
  }
  if (t.fonts !== undefined && c.object(t.fonts, '$.fonts', [], ['display', 'body'])) {
    if (t.fonts.display !== undefined) c.oneOf(t.fonts.display, '$.fonts.display', FONT_NAMES);
    if (t.fonts.body !== undefined) c.oneOf(t.fonts.body, '$.fonts.body', FONT_NAMES);
  }
  if (t.seatPieces !== undefined && c.object(t.seatPieces, '$.seatPieces', [], Object.keys(t.seatPieces as object)))
    for (const [k, v] of Object.entries(t.seatPieces)) { c.string(k, '$.seatPieces key', 128, ID); c.oneOf(v, `$.seatPieces.${k}`, SEAT_PIECES); }
  if (t.textRoles !== undefined && c.object(t.textRoles, '$.textRoles', [], [...TEXT_ROLES]))
    for (const [role, style] of Object.entries(t.textRoles)) {
      const p = `$.textRoles.${role}`;
      if (!c.object(style, p, [], ['font', 'weight', 'size', 'italic', 'caps'])) continue;
      if (style!.font !== undefined) c.oneOf(style!.font, `${p}.font`, ['display', 'body', 'mono', ...FONT_NAMES]);
      if (style!.weight !== undefined) c.number(style!.weight, `${p}.weight`, 100, 900, true);
      if (style!.size !== undefined) c.number(style!.size, `${p}.size`, 1, 40);
      if (style!.italic !== undefined && typeof style!.italic !== 'boolean') c.error(`${p}.italic`, 'Expected true or false.');
      if (style!.caps !== undefined && typeof style!.caps !== 'boolean') c.error(`${p}.caps`, 'Expected true or false.');
    }
}

export function contrast(a: string, b: string): number {
  const lum = (hex: string) => {
    const ch = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
  };
  const [l1, l2] = [lum(a), lum(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

// ---- Reference checks against a Describe result and recorded observations ----------------

interface FixtureZone { id: string; kind?: string; owner?: string; board?: string }
interface FixtureObservation { ui: { zones: FixtureZone[]; counters: { key?: string; owner?: string }[]; objects?: { kind?: string; catalog?: string; tags?: string[]; zone?: string; face?: string }[] } }
interface FixtureDescription { catalog: { key: string; kind: string; tags?: string[]; fields?: Record<string, unknown> }[]; resources: { key: string }[]; boards: { id: string }[]; seats: { id: string }[] }

export function validateReferences(pkg: { layouts: Layouts; templates: Templates; theme: Theme; assetIds: Set<string> }, description: FixtureDescription, observations: FixtureObservation[]): Issue[] {
  const c = new Checker('references');
  const seats = description.seats.map(s => s.id);
  // Every zone seen in any observation should be placed by a selector other than "rest".
  const zones = new Map<string, FixtureZone>();
  for (const o of observations) for (const z of o.ui.zones) zones.set(z.id, z);
  const placed = new Set<string>();
  const visit = (node: LayoutNode, seat: string | null) => {
    switch (node.type) {
      case 'row': case 'column': case 'grid': node.children.forEach(ch => visit(ch, seat)); break;
      case 'tabs': node.tabs.forEach(t => visit(t.child, seat)); break;
      case 'panel': visit(node.child, seat); break;
      case 'repeat': for (const s of seats) visit(node.child, s); break;
      case 'component': {
        if (!node.select) break;
        const s = parseSelector(node.select);
        if (typeof s === 'string' || s.kind === 'rest') break;
        let matched = 0;
        for (const z of zones.values()) {
          const sub = (text: string) => text.replaceAll('{seat}', seat ?? '');
          const hit = s.kind === 'zone' ? z.id === sub(s.id) : s.kind === 'zonePrefix' ? z.id.startsWith(sub(s.prefix))
            : s.kind === 'zonesOwner' ? z.owner === sub(s.seat) : s.kind === 'zonesKind' ? z.kind === s.zoneKind
            : s.kind === 'board' ? z.kind === 'board' && z.board === sub(s.id) : false;
          if (hit) { placed.add(z.id); matched++; }
        }
        if (s.kind === 'countersOwner' || s.kind === 'countersKey') {
          const sub = (text: string) => text.replaceAll('{seat}', seat ?? '');
          const any = observations.some(o => o.ui.counters.some(c => s.kind === 'countersOwner' ? c.owner === sub(s.seat) : (c.key ?? '').startsWith(sub(s.prefix))));
          if (!any && !node.select.includes('{self}')) c.warn(node.select, 'Matches no counter in the recorded observations.');
          break;
        }
        if (s.kind === 'board' && !description.boards.some(b => b.id === s.id.replaceAll('{seat}', seat ?? ''))) c.error(node.select, 'No Describe board has this ID.');
        if (matched === 0 && ['zone', 'zonePrefix', 'zonesOwner', 'zonesKind', 'board'].includes(s.kind) && !node.select.includes('{self}'))
          c.warn(node.select, `Matches no zone in the recorded observations${seat ? ` for seat ${seat}` : ''}.`);
      }
    }
  };
  visit(pkg.layouts.layouts.wide, null);
  for (const z of zones.values()) if (!placed.has(z.id)) c.warn(`zone ${z.id}`, 'Only the "rest" selector places this zone.');

  // Every catalog entry should match some template; asset placeholders need a file or a fallback.
  for (const entry of description.catalog) {
    const tpl = pkg.templates.templates.find(t => (!t.match.kind || t.match.kind === entry.kind) && (!t.match.catalog || t.match.catalog.includes(entry.key)) && (!t.match.tags || t.match.tags.every(tag => entry.tags?.includes(tag))));
    if (!tpl) { c.warn(`catalog ${entry.key}`, 'No template matches this catalog entry; the default shape is used.'); continue; }
    for (const [slot, text] of Object.entries(tpl.slots)) {
      const b = parseBinding(text);
      if (typeof b === 'string' || b.source.kind !== 'asset' || !b.source.id.includes('{')) continue;
      const id = expandAsset(b.source.id, { catalog: entry.key, kind: entry.kind });
      if (id && !pkg.assetIds.has(id)) c.warn(`template ${tpl.id}.${slot}`, `No asset "${id}" for ${entry.key}; the slot stays empty.`);
    }
    for (const text of Object.values(tpl.slots)) {
      const b = parseBinding(text);
      if (typeof b === 'string') continue;
      for (const f of b.formatters) if (f.kind === 'costRow' && !description.resources.some(r => Object.keys({ ...entry.fields }).some(k => k === f.prefix + r.key)) && entry.fields && Object.keys(entry.fields).some(k => k.startsWith(f.prefix)) === false) {
        // An entry without any cost is fine; this check only reports prefixes that never match any resource key.
      }
    }
  }
  const prefixes = new Set<string>();
  for (const t of pkg.templates.templates) for (const text of Object.values(t.slots)) {
    const b = parseBinding(text);
    if (typeof b !== 'string') for (const f of b.formatters) if (f.kind === 'costRow') prefixes.add(f.prefix);
  }
  for (const prefix of prefixes) {
    const used = description.catalog.some(e => Object.keys(e.fields ?? {}).some(k => description.resources.some(r => k === prefix + r.key)));
    if (!used) c.warn(`costRow:${prefix}`, 'No catalog field uses this prefix with a Describe resource key.');
  }
  for (const r of description.resources) if (!pkg.theme.icons?.[r.key]) c.warn(`resource ${r.key}`, 'No theme icon; resources are drawn as text.');
  return c.issues;
}
